import React, { useEffect, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Row, Container, Button, ListGroupItem, ListGroup } from 'reactstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTv, faSlidersH, faPlus, faMapMarkerAlt, faChartLine } from '@fortawesome/free-solid-svg-icons';

import PlayerSettingsModal from './PlayerSettingsModal';
import UserSettingsModal from './UserSettingsModal';
import HelpModal from './HelpModal';
import PlayerCodeEntry from './PlayerCodeEntry';

import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import { authContext } from '../contexts/AuthContext';

import AppHeader from './AppHeader';
import FooterContent from './FooterContent';
import { readRecentJukeboxes } from '../utils/RecentJukeboxes';

import './HomeView.scss';

interface IProps {}

interface HomeViewState {
  userSettingsModal: boolean;
  playerSettingsModal: boolean;
  helpModal: boolean;
  editedPlayer: any;
  dataLoaded: boolean;
}

type HomeViewProps = IProps;

const HomeView = (props) => {
  const [userSettingsModal, setUserSettingsModal] = useState(false);
  const [playerSettingsModal, setPlayerSettingsModal] = useState(false);
  const [helpModal, setHelpModal] = useState(false);
  const [myPlayers, setMyPlayers] = useState([]);
  const [recentJukeboxes] = useState(readRecentJukeboxes);
  const context = useContext(authContext);
  const navigate = useNavigate();
  let currentUser = context.currentUser;
  console.log('currentUser is ', currentUser);

  useEffect(() => {
    refreshUser();
    refreshPlayers();
  }, []);

  const refreshUser = () => {
    console.log('refreshUser');
    const token = context.auth.token;
    api
      .users('me', 'GET', token, null)
      .then((response) => {
        context.updateCurrentUser(response);
      })
      .catch((error) => {
        console.log('There has been a problem with your refreshUser operation: ' + error.message);
      });
  };

  const refreshPlayers = () => {
    console.log('refreshPlayers');
    const token = context.auth.token;
    api
      .users('myPlayers?include=admin', 'GET', token, null)
      .then((response) => {
        setMyPlayers(response);
      })
      .catch((error) => {
        console.log('There has been a problem with your myPlayers operation: ' + error.message);
      });
  };

  const handleControlPanelButtonClick = (player: any, e: any) => {
    context.setSelectedPlayer({ id: player.id, name: player.name, playerIdentifier: player.playerIdentifier });
    navigate(`/control/${player.playerIdentifier}`);
  };

  const handleGenerateNewPlayerButtonClicked = (e: any) => {
    console.log('handleGenerateNewPlayerButtonClicked');
    const currentModalState = playerSettingsModal;
    setPlayerSettingsModal(!currentModalState);
  };

  const handleShowUsernameButtonClicked = (e: any) => {
    console.log('handleShowUsernameButtonClicked');
    toggleUserSettingsModal();
  };

  const handleHelpButtonClicked = (e: any) => {
    console.log('handleHelpButtonClicked');
    toggleHelpModal();
  };

  const handleLogoutButtonClicked = (e: any) => {
    console.log('handleLogoutButtonClicked');
    trackEvent('logout');
    const token = context.auth.token;
    api
      .users('logout', 'POST', token, null)
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.log('There has been a problem with your logout operation: ' + error.message);
      });
    context.setUnauthStatus();
    // Accounts.logout();
  };

  const handleLaunchPlayerButtonClick = (player: any, e: any) => {
    // Session.setPersistent('selectedPlayerIdentifier', player.playerIdentifier);
    // context
    context.setSelectedPlayer({ id: player.id, name: player.name, playerIdentifier: player.playerIdentifier });
    navigate(`/player/${player.playerIdentifier}`);
  };

  const toggleUserSettingsModal = () => {
    const currentModalState = userSettingsModal;
    setUserSettingsModal(!currentModalState);
    refreshUser();
  };

  const togglePlayerSettingsModal = () => {
    const currentModalState = playerSettingsModal;
    setPlayerSettingsModal(!currentModalState);
    if (currentModalState) {
      console.log('Refresh players');
      refreshPlayers();
    }
  };

  const toggleHelpModal = () => {
    const currentModalState = helpModal;
    setHelpModal(!currentModalState);
  };

  const isSiteAdmin = !!currentUser && currentUser.role === 'admin';
  const hasJukeboxes = !!myPlayers && myPlayers.length > 0;
  const myCodes = (myPlayers || []).map((player) => player.playerIdentifier);
  // Your own jukeboxes are listed below already, so they are left out of this list.
  const recent = recentJukeboxes.filter((item) => myCodes.indexOf(item.code) === -1);

  return (
    <Container fluid style={{ paddingLeft: '0px', paddingRight: '0px' }}>
      {helpModal && <HelpModal isOpen={helpModal} onToggleModal={toggleHelpModal} />}
      {userSettingsModal && (
        <UserSettingsModal
          currentUser={currentUser}
          isOpen={userSettingsModal}
          onToggleModal={toggleUserSettingsModal}
        />
      )}
      {playerSettingsModal && (
        <PlayerSettingsModal
          isOpen={playerSettingsModal}
          onToggleModal={togglePlayerSettingsModal}
        />
      )}
      <AppHeader back={false} />

      <Row className="g-0">
        <div className="col">
          <div className="public-page-content home-page-content">
            <div className="public-page-formwrapper">
              <div>
                {/* Jookable's own admins only. The server checks too. */}
                {isSiteAdmin && (
                  <Link className="home-dashboard" to="/admin">
                    <FontAwesomeIcon icon={faChartLine} /> Jookable dashboard
                  </Link>
                )}

                {/* First on the screen because it is what a customer in a venue is here
                    for. Running a jukebox of your own is the rarer case and sits below. */}
                <div className="connect-player">
                  <h3 style={{ paddingTop: 10 }}>{'Add songs'}</h3>
                  <PlayerCodeEntry hint={'Enter the code shown on the TV screen.'} />
                  {recent.length > 0 && (
                    <div className="home-recent">
                      <div className="home-section-label">Recent jukeboxes</div>
                      <ul className="home-recent-list">
                        {recent.map((item) => (
                          <li key={item.code}>
                            <Link className="home-recent-link" to={`/jukebox/${item.code}`}>
                              <span className="home-recent-name">{item.name}</span>
                              <span className="home-recent-code">{item.code.toUpperCase()}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Link className="home-text-link" to="/venues">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> Find venues near you
                  </Link>
                </div>

                {hasJukeboxes ? (
                  <div className="home-jukeboxes">
                    <h3 style={{ paddingTop: 10 }}>{'Your jukeboxes'}</h3>
                    <ListGroup className="settings-nav" style={{ paddingLeft: 0 }}>
                      {myPlayers.map((player) => (
                        <ListGroupItem className="home-jukebox-row" key={player.id}>
                          <div className="home-jukebox-name">
                            {player.name}
                            <span className="home-recent-code">{player.playerIdentifier.toUpperCase()}</span>
                            {player.role === 'admin' && <span className="home-jukebox-role">Admin</span>}
                          </div>
                          <div className="home-jukebox-actions">
                            <Button
                              className="px-2 px-sm-3"
                              outline
                              color="danger"
                              onClick={(e) => handleLaunchPlayerButtonClick(player, e)}
                            >
                              <FontAwesomeIcon icon={faTv} /> TV screen
                            </Button>
                            <Button
                              className="px-2 px-sm-3"
                              outline
                              color="danger"
                              onClick={(e) => handleControlPanelButtonClick(player, e)}
                            >
                              <FontAwesomeIcon icon={faSlidersH} /> Control panel
                            </Button>
                          </div>
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                    <button type="button" className="home-text-link" onClick={handleGenerateNewPlayerButtonClicked}>
                      <FontAwesomeIcon icon={faPlus} /> New jukebox
                    </button>
                  </div>
                ) : (
                  <button type="button" className="home-text-link" onClick={handleGenerateNewPlayerButtonClicked}>
                    <FontAwesomeIcon icon={faPlus} /> Run Jookable at your venue
                  </button>
                )}

                <ListGroup className="settings-nav home-account-row" style={{ padding: 0, paddingTop: 20, flexDirection: 'row' }}>
                  <ListGroupItem tag="button" action onClick={handleShowUsernameButtonClicked}>
                    {'Profile'}
                  </ListGroupItem>
                  <ListGroupItem tag="button" action onClick={handleHelpButtonClicked}>
                    {'Help'}
                  </ListGroupItem>
                  <ListGroupItem tag="button" action onClick={handleLogoutButtonClicked}>
                    {'Log Out'}
                  </ListGroupItem>
                </ListGroup>
                <p className="home-tagline">
                  {'Host your own jukebox on any TV, then add videos from any phone or computer.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Row>

      <Row className="g-0">
        <div className="col footer">
          <FooterContent />
        </div>
      </Row>
    </Container>
  );
};

export default HomeView;
